import React from 'react';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IFormTextarea } from './types';

import { classList } from '@/helpers';
import { InputDescription } from './components';

const Textarea = ({
  name,
  control,
  label,
  resizeVertical = false,
  required = false,
  className = '',
  showLimit,
  showLimitInRed,
  customLimitMessage,
  ...rest
}: IFormTextarea): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <fieldset>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { isTouched, error } }) => (
          <>
            <Label htmlFor={rest.id || name}>
              {required && <span className="text-danger me-1">*</span>}
              {label}
            </Label>
            <StyledTextarea
              {...rest}
              name={name}
              id={rest.id || name}
              className={classList(resizeVertical && 'resize', error && 'error', className)}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
            {rest.maxLength && showLimit && (
              <CharCount
                className={`justify-content-start limit ${
                  showLimitInRed && Number(field?.value?.length ?? 0) === rest.maxLength ? 'exceeded' : ''
                }`}
              >
                {Number(field?.value?.length ?? 0) === rest.maxLength && customLimitMessage
                  ? customLimitMessage
                  : t('common.limit')}{' '}
                {`${field?.value?.length ?? 0}/${rest.maxLength}`}
              </CharCount>
            )}
            {rest.description && <InputDescription>{rest.description}</InputDescription>}
            <ErrorMessage>{(error && isTouched) || error?.type === 'required' ? error.message : ''}</ErrorMessage>
          </>
        )}
      />
    </fieldset>
  );
};

export { Textarea };

const StyledTextarea = styled.textarea`
  width: 100%;
  height: 120px;
  min-height: 120px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  resize: none;
  background-color: #ffffff;
  padding: 2px 16px;
  margin-top: 4px;

  &:read-only {
    color: #bbbcbd;
    background-color: #f9fafb;
  }

  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgb(0 123 255 / 25%);
  }

  &.resize {
    resize: vertical;
  }

  &.error {
    border-color: red;
  }
`;

const ErrorMessage = styled.div.attrs({
  className: 'text-danger pt-1',
})`
  font-size: 12px;
  line-height: 12px;
  min-height: 24px;
`;

const Label = styled.label`
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 4px;
  color: #374151;
`;

const CharCount = styled.div`
  display: flex;
  justify-content: flex-end;
  &.limit {
    font-size: 13px;
    line-height: 16px;
    color: #57616b;
  }
  &.exceeded {
    color: red;
  }
`;
